import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { brandingAPI, resolveAssetUrl, type Branding, type BrandingLogin } from '../services/api';

/**
 * BrandingContext — identidade visual do cliente carregada do backend
 * (CLIENT_NAME + CLIENT_LOGO_URL via /api/branding).
 *
 * - Cache em localStorage pra render instantâneo no boot (evita flash do
 *   "Farisx" default antes do fetch terminar).
 * - `refresh()` é chamado depois de salvar em Configurações > Aplicação
 *   pra propagar a mudança sem reload.
 * - Expõe `logoSrc` já resolvido (URL absoluta) — componente só usa.
 */

interface BrandingContextValue {
  clientName: string;
  logoSrc: string | null;
  /** Textos da tela de login (Fase 28) */
  login: BrandingLogin | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const DEFAULT: Branding = { clientName: 'Farisx', logoUrl: null, login: null };
const STORAGE_KEY = 'farisx_branding';

function readCached(): Branding {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    return { ...DEFAULT, ...(JSON.parse(raw) as Partial<Branding>) };
  } catch {
    return DEFAULT;
  }
}

const BrandingContext = createContext<BrandingContextValue>({
  clientName: DEFAULT.clientName,
  logoSrc: null,
  login: null,
  loading: false,
  refresh: async () => undefined,
});

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<Branding>(() => readCached());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await brandingAPI.get();
      setBranding(r);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(r));
      } catch {
        /* quota — ignora */
      }
    } catch {
      // mantém cache anterior
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Atualiza <title> automaticamente
  useEffect(() => {
    if (branding.clientName) {
      document.title = branding.clientName;
    }
  }, [branding.clientName]);

  const value = useMemo<BrandingContextValue>(
    () => ({
      clientName: branding.clientName,
      logoSrc: resolveAssetUrl(branding.logoUrl),
      login: branding.login ?? null,
      loading,
      refresh,
    }),
    [branding, loading, refresh],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}
